import React, {
  memo,
  useRef,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import {
  Page,
  DeleteDialog,
  useMediaQuery,
  Box,
  Alert,
} from "../../../components";
import { useDispatch, useSelector } from "react-redux";
import useAuthorized, {
  SUPPRESSION_DELETE_PERMISSION,
} from "../../../hooks/useAuthorized";
import Actions from "./Actions";
import SuppressionBase from "./Base";
import {
  clearSuppressions,
  deleteSuppression,
  listSuppressions,
} from "../../../actions/suppression";
import SuppressionToggle from "../SuppressionToggle";
import { Grid } from "../../../components";
import { useTableActions } from "../../../hooks/useTableActions";
import {
  CustomDeleteConfirmButton,
  CustomDeleteDialogContentText,
  CustomDeleteDialogTitle,
} from "./CustomDeleteComponents";
import { notify } from "../../../actions/notification";
import {
  defaultChannelType,
  onInputTabChange,
  SUPPRESSION_TYPE_ENUM,
  suppressionTypeMapMsg,
} from "../util";

const useStyles = makeStyles((theme) => ({
  alert: {
    marginBottom: theme.spacing(2),
  },
}));

const getExtraColumns = ({ t, onOpenDeleteDialog, canDelete }) => [
  {
    name: "actions",
    getter: (suppressed) => (
      <Actions
        canDelete={canDelete}
        onDelete={onOpenDeleteDialog}
        suppressed={suppressed}
      />
    ),
    label: t("Actions"),
    sortable: false,
    width: 0.7,
    align: "center",
  },
];

function SuppressionListsPage({
  type,
  shortCodes,
  domainAddresses,
  callerIds,
  canCreate,
  canList,
  missingConfigMessage,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const listsRef = useRef();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const history = useHistory();
  const suppressionList = useSelector((state) => state.suppression);
  const queryParams = useSelector((state) => state.params);

  const canDelete = useAuthorized(SUPPRESSION_DELETE_PERMISSION);

  const [suppressionToDelete, setSuppressionToDelete] = useState();

  const {
    onItemsPerPageChange,
    onPageChange,
    onSearchChange,
  } = useTableActions();

  const onOpenDeleteDialog = useCallback(
    (suppressed) => setSuppressionToDelete(suppressed),
    [setSuppressionToDelete]
  );

  const onCloseDeleteDialog = useCallback(() => setSuppressionToDelete(null), [
    setSuppressionToDelete,
  ]);

  const getSuppressed = useCallback(async () => {
    if (missingConfigMessage) {
      dispatch(clearSuppressions());
      return;
    }
    try {
      if (type === SUPPRESSION_TYPE_ENUM.SMS) {
        await dispatch(listSuppressions({ shortCodes, params: queryParams }));
      } else if (type === SUPPRESSION_TYPE_ENUM.EMAIL) {
        await dispatch(
          listSuppressions({ domainAddresses, params: queryParams })
        );
      } else if (type === SUPPRESSION_TYPE_ENUM.VOICE) {
        await dispatch(listSuppressions({ callerIds, params: queryParams }));
      }
    } catch (err) {
      dispatch(
        notify({
          type: "error",
          message: err.message,
        })
      );
    }
  }, [
    dispatch,
    missingConfigMessage,
    queryParams,
    shortCodes,
    domainAddresses,
    callerIds,
    type,
  ]);

  const onDelete = useCallback(async () => {
    const { _id } = suppressionToDelete;
    await dispatch(deleteSuppression({ _id, type }));

    await getSuppressed();
    onCloseDeleteDialog();
  }, [
    dispatch,
    suppressionToDelete,
    type,
    getSuppressed,
    onCloseDeleteDialog,
  ]);

  useEffect(() => {
    getSuppressed();
  }, [getSuppressed]);

  const extraColumns = useMemo(
    () =>
      getExtraColumns({
        t,
        onOpenDeleteDialog,
        canDelete,
      }),
    [t, onOpenDeleteDialog, canDelete]
  );

  const deleteDialogTitle = useCallback(() => {
    if (!type) {
      return null;
    }
    const suppressionType =
      type === defaultChannelType
        ? type.toUpperCase()
        : type[0].toUpperCase() + type.slice(1);
    return <CustomDeleteDialogTitle suppressionTab={suppressionType} />;
  }, [type]);
  const deleteDialogContentText = useCallback(() => {
    if (!suppressionToDelete) {
      return null;
    }
    return (
      <CustomDeleteDialogContentText
        suppressionToDelete={suppressionToDelete}
      />
    );
  }, [suppressionToDelete]);
  const deleteDialogConfirmButton = useCallback(
    () => <CustomDeleteConfirmButton onDelete={onDelete} />,
    [onDelete]
  );

  return (
    <Page screenSized={!isSmallScreen}>
      <Grid direction="column" spacing={2}>
        <SuppressionToggle
          onTabChange={onInputTabChange(history)}
          activeTab={type}
          errors={""}
          readOnly={false}
        />
      </Grid>

      <Box p={2}>
        {missingConfigMessage && (
          <Alert
            severity="error"
            variant="outlined"
            className={classes.alert}
            data-test-id="suppression-missing-config-alert"
          >
            {t(missingConfigMessage)}
          </Alert>
        )}
        <SuppressionBase
          disableTable={!canList}
          ref={listsRef}
          extraColumns={extraColumns}
          suppressionList={suppressionList ? suppressionList.list : []}
          canCreate={canCreate}
          onItemsPerPageChange={onItemsPerPageChange}
          onSearchChange={onSearchChange}
          onPageChange={onPageChange}
          suppressionCount={suppressionList ? suppressionList.count : 0}
          type={type}
        />
        <DeleteDialog
          open={Boolean(suppressionToDelete)}
          onClose={onCloseDeleteDialog}
          onDelete={onDelete}
          name={suppressionToDelete?.to}
          resource={t(suppressionTypeMapMsg[type])}
          DialogTitle={deleteDialogTitle}
          DialogContentText={deleteDialogContentText}
          DialogConfirmButton={deleteDialogConfirmButton}
        ></DeleteDialog>
      </Box>
    </Page>
  );
}

export default memo(SuppressionListsPage);
